import clsx from "clsx"
import { FC, ReactNode } from "react"
import { defineMessage, useIntl } from "react-intl"
import { isBrowserName } from "../../utils/domHelpers/browserEnv"
import { FCC } from "../../utils/reactHelpers/types"
import { assertNever } from "../../utils/types"
import { NavLink } from "../NavLink"
import { GradientFilledButton } from "../button/variants/GradientFilledButton/GradientFilledButton"

export type UnSupportAlertType = "browser" | "wallet" | "maintenance"

export interface UnSupportAlertModalContentProps {
  type: UnSupportAlertType
  onClose?: () => void
}

export const UnSupportAlertModalContent: FC<
  UnSupportAlertModalContentProps
> = props => {
  const info = useCopyFromUnsupportedType(props.type)

  return (
    <div
      className={clsx(
        "flex flex-col items-center px-6 py-10 gap-6 sm:w-[400px] sm:h-[300px]",
        "text-black bg-white rounded-xl",
      )}
    >
      {props.onClose && (
        <div className={"absolute -translate-y-4 self-end"}>
          <span
            className={
              "absolute -translate-x-1/2 -translate-y-1/2 p-2.5 cursor-pointer hover:opacity-50 leading-none text-[24px] text-gray-400"
            }
            onClick={props.onClose}
          >
            &times;
          </span>
        </div>
      )}

      <div className="flex items-center">
        <svg
          className={"inline-block"}
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.6665 3.7332C18.6665 3.86654 18.9332 4.1332 19.3332 4.79987C19.7332 5.46654 20.1332 6.1332 20.6665 7.19987C21.1999 8.26654 21.8665 9.19987 22.5332 10.3999C23.1999 11.5999 23.9999 12.7999 24.6665 13.9999C25.3332 15.1999 26.1332 16.5332 26.7999 17.7332C27.4665 18.9332 28.1332 20.1332 28.7999 21.0665C29.3332 22.1332 29.8665 22.9332 30.3999 23.7332C30.9332 24.5332 31.0665 24.9332 31.3332 25.1999C31.5999 25.7332 31.8665 26.2665 31.8665 26.7999C31.9999 27.3332 31.8665 27.7332 31.7332 28.2665C31.5999 28.7999 31.3332 29.0665 31.0665 29.3332C30.6665 29.5999 30.2665 29.7332 29.7332 29.7332H2.79987C1.99987 29.7332 1.3332 29.5999 0.933203 29.3332C0.533203 29.0665 0.266536 28.6665 0.133203 28.2665C-0.000130206 27.8665 -0.000130206 27.3332 0.133203 26.9332C0.266536 26.3999 0.39987 25.9999 0.79987 25.4665C0.933203 25.1999 1.19987 24.7999 1.59987 23.9999C1.99987 23.3332 2.5332 22.3999 3.19987 21.4665C3.86654 20.3999 4.5332 19.3332 5.19987 18.1332C5.86654 16.9332 6.66654 15.5999 7.46654 14.3999C8.26654 13.1999 8.9332 11.8665 9.59987 10.6665C10.2665 9.46654 10.9332 8.39987 11.4665 7.46654C11.9999 6.5332 12.5332 5.7332 12.9332 5.06654L13.5999 3.99987C13.8665 3.59987 14.2665 3.19987 14.7999 2.9332C15.3332 2.66654 15.7332 2.5332 16.2665 2.5332C16.7999 2.5332 17.1999 2.66654 17.7332 2.79987C17.9999 2.9332 18.3999 3.3332 18.6665 3.7332ZM17.8665 9.86654C17.8665 9.59987 17.8665 9.3332 17.7332 9.19987C17.5999 8.9332 17.4665 8.79987 17.3332 8.66654C17.1999 8.5332 16.9332 8.39987 16.6665 8.26654C16.3999 8.1332 16.1332 7.99987 15.8665 7.99987C15.3332 7.99987 14.9332 8.1332 14.5332 8.5332C14.1332 8.9332 13.8665 9.3332 13.8665 9.86654V18.1332C13.8665 18.6665 14.1332 19.0665 14.5332 19.4665C14.9332 19.8665 15.3332 19.9999 15.8665 19.9999C16.3999 19.9999 16.7999 19.8665 17.1999 19.4665C17.5999 19.0665 17.8665 18.6665 17.8665 18.1332V9.86654ZM15.8665 21.8665C15.3332 21.8665 14.7999 21.9999 14.5332 22.3999C14.2665 22.7999 13.9999 23.1999 13.9999 23.7332C13.9999 24.2665 14.1332 24.7999 14.5332 25.0665C14.9332 25.4665 15.3332 25.5999 15.8665 25.5999C16.3999 25.5999 16.9332 25.4665 17.1999 25.0665C17.5999 24.6665 17.7332 24.2665 17.7332 23.7332C17.7332 23.1999 17.5999 22.6665 17.1999 22.3999C16.9332 21.9999 16.3999 21.8665 15.8665 21.8665Z"
            fill="#F24750"
          />
          <path
            d="M17.8667 9.86667C17.8667 9.6 17.8667 9.33333 17.7334 9.2C17.6 8.93333 17.4667 8.8 17.3334 8.66667C17.2 8.53333 16.9334 8.4 16.6667 8.26667C16.4 8.13333 16.1334 8 15.8667 8C15.3334 8 14.9334 8.13333 14.5334 8.53333C14.1334 8.93333 13.8667 9.33333 13.8667 9.86667V18.1333C13.8667 18.6667 14.1334 19.0667 14.5334 19.4667C14.9334 19.8667 15.3334 20 15.8667 20C16.4 20 16.8 19.8667 17.2 19.4667C17.6 19.0667 17.8667 18.6667 17.8667 18.1333V9.86667ZM15.8667 21.8667C15.3334 21.8667 14.8 22 14.5334 22.4C14.2667 22.8 14 23.2 14 23.7333C14 24.2667 14.1334 24.8 14.5334 25.0667C14.9334 25.4667 15.3334 25.6 15.8667 25.6C16.4 25.6 16.9334 25.4667 17.2 25.0667C17.6 24.6667 17.7334 24.2667 17.7334 23.7333C17.7334 23.2 17.6 22.6667 17.2 22.4C16.9334 22 16.4 21.8667 15.8667 21.8667Z"
            fill="white"
          />
        </svg>

        <p className="text-center text-xl font-bold ml-2">{info.title}</p>
      </div>

      <div className={"flex-1 flex items-center"}>
        <p className="text-gray-600 text-center">{info.text}</p>
      </div>

      {info.hint && (
        <div className="flex-1 flex items-center">
          <p className="text-gray-500 text-center">{info.hint}</p>
        </div>
      )}

      <div className={"mt-auto"}>{info.button}</div>
    </div>
  )
}

const ActionButton: FCC<{ onClick?: () => void }> = props => (
  <button onClick={props.onClick}>{props.children}</button>
)

function useCopyFromUnsupportedType(type: UnSupportAlertType): {
  title: ReactNode
  text: ReactNode
  hint?: ReactNode
  button: ReactNode
} {
  const { $t } = useIntl()
  switch (type) {
    case "browser":
      return {
        title: $t(
          defineMessage({
            defaultMessage: "Browser not supported",
            description:
              "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/browser title",
          }),
        ),
        text: $t(
          defineMessage({
            defaultMessage:
              "Use Google Chrome to access alexlab.app for Web experience.Alternatively, download Google Chrome on your desktop computer.",
            description:
              "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Browser text",
          }),
        ),
        button: (
          <ActionButton
            onClick={() => {
              window.open("https://www.google.com/chrome/", "_self")
            }}
          >
            {$t(
              defineMessage({
                defaultMessage: "Get Google Chrome for Desktop",
                description:
                  "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Browser button",
              }),
            )}
          </ActionButton>
        ),
      }
    case "wallet":
      return {
        title: $t(
          defineMessage({
            defaultMessage: "Wallet is not installed",
            description:
              "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Wallet title",
          }),
        ),
        text: (
          <span className="text-gray-900">
            {$t(
              defineMessage({
                defaultMessage:
                  "ALEX requires Stacks Wallet to run properly. Please install the wallet browser extension and refresh.",
                description:
                  "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Wallet text",
              }),
            )}
          </span>
        ),
        button: (
          <div className="flex flex-col gap-y-1 -mt-1">
            <NavLink
              className={"hidden"}
              to={"https://www.xverse.app/download"}
            >
              <GradientFilledButton>
                {$t(
                  defineMessage({
                    defaultMessage: "Get Xverse Wallet for Chrome",
                    description:
                      "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Wallet button",
                  }),
                )}
              </GradientFilledButton>
            </NavLink>
            <NavLink
              to={
                isBrowserName("Firefox")
                  ? "https://addons.mozilla.org/zh-CN/firefox/addon/hiro-wallet/"
                  : "https://chrome.google.com/webstore/detail/hiro-wallet/ldinpeekobnhjjdofggfgjlcehhmanlj"
              }
            >
              <GradientFilledButton>
                {isBrowserName("Firefox")
                  ? $t(
                      defineMessage({
                        defaultMessage: "Get Hiro Wallet for Firefox",
                        description:
                          "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Wallet button",
                      }),
                    )
                  : $t(
                      defineMessage({
                        defaultMessage: "Get Hiro Wallet for Chrome",
                        description:
                          "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Wallet button",
                      }),
                    )}
              </GradientFilledButton>
            </NavLink>
          </div>
        ),
      }
    case "maintenance":
      return {
        title: $t(
          defineMessage({
            defaultMessage: "Maintenance Mode",
            description:
              "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Maintenance title",
          }),
        ),
        text: $t(
          defineMessage({
            defaultMessage:
              "ALEX is currently undergoing maintenance. Please check back later.",
            description:
              "/Conponents/UnSupportAlertModal/CopyFromUnsupportedType/Maintenance text",
          }),
        ),
        button: null,
      }
    default:
      assertNever(type)
  }
}
